// src/admin/components/StudentProfileDrawer.jsx
import React, { useState, useEffect } from "react";
import {
  X,
  Mail,
  Phone,
  MapPin,
  Calendar,
  CheckCircle2,
  BookOpen,
  ChevronRight,
  UserCheck,
} from "lucide-react";
import { resultsAPI } from "../services/api";
import { useAdminData } from "../context/AdminDataContext";

export default function StudentProfileDrawer({
  isOpen,
  onClose,
  student,
  mode = "all", // "prelims" | "mains" | "all"
  onInspectPrelimsResult,
  onInspectMainsSubmission,
}) {
  const { studentResults } = useAdminData();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(mode === "mains" ? "mains" : "prelims");

  useEffect(() => {
    if (mode === "mains") setActiveTab("mains");
    else if (mode === "prelims") setActiveTab("prelims");
  }, [mode, isOpen]);

  useEffect(() => {
    if (!isOpen || !student) {
      setProfile(null);
      return;
    }

    const fetchProfile = async () => {
      setLoading(true);
      const studentIdentifier =
        student.studentId ||
        student.id ||
        student.email ||
        student.studentEmail ||
        student.regId ||
        student.studentName;

      try {
        if (studentIdentifier) {
          const res = await resultsAPI.getStudentProfile(studentIdentifier);
          if (res?.success && res.data) {
            setProfile(res.data);
            setLoading(false);
            return;
          }
        }
      } catch (err) {
        console.warn("Could not fetch remote student profile, computing locally:", err.message);
      }

      // Fallback calculation using context studentResults
      const sEmail = (student.email || student.studentEmail || "").toLowerCase();
      const sName = (student.studentName || student.name || "").toLowerCase();
      const sReg = (student.regId || "").toLowerCase();

      const matchedResults = (studentResults || []).filter((r) => {
        if (!r) return false;
        if (sEmail && (r.email || r.studentEmail || "").toLowerCase() === sEmail) return true;
        if (sReg && (r.regId || "").toLowerCase() === sReg) return true;
        if (sName && (r.studentName || "").toLowerCase() === sName) return true;
        return false;
      });

      const prelims = matchedResults.filter((r) => r.examType === "Prelims");
      const mains = matchedResults.filter((r) => r.examType === "Mains");

      const localProfile = {
        personalInfo: {
          id: student.id || student.studentId || `std-${Date.now()}`,
          name: student.studentName || student.name || "UPSC Aspirant",
          email: student.email || student.studentEmail || "student@example.com",
          phone: student.phone || student.mobileNumber || "+91 98401 22334",
          mobileNumber: student.mobileNumber || student.phone || "+91 98401 22334",
          registrationId: student.regId || `SIA-2026-${Math.floor(1000 + Math.random() * 9000)}`,
          location: student.location || "Chennai, Tamil Nadu",
          registeredAt: student.registeredAt || student.submissionDate || "2026-01-15",
          avatar: student.avatar || "",
        },
        academicSummary: {
          totalPrelimsAttended: prelims.length,
          totalPrelimsCompleted: prelims.length,
          totalMainsSubmissions: mains.length,
          totalMainsEvaluated: mains.filter((m) => m.evaluationStatus === "Evaluated" || m.resultStatus === "Evaluated").length,
        },
        prelimsHistory: prelims.map((p) => ({
          id: p.id || p._id,
          testId: p.testId,
          testName: p.initiativeName || p.testName,
          date: p.submissionDate,
          marks: p.marksObtained,
          totalMarks: p.totalMarks || 10,
          percentage: p.percentage || Math.round(((p.marksObtained || 0) / (p.totalMarks || 10)) * 100),
          correctAnswers: p.correctCount,
          incorrectAnswers: p.incorrectCount,
          unanswered: p.unansweredCount || 0,
          answers: p.answers,
        })),
        mainsHistory: mains.map((m) => ({
          id: m.id || m._id,
          initiativeId: m.initiativeId,
          initiativeName: m.initiativeName || m.initiativeTitle,
          gsPaper: m.gsPaper || "GS Paper II",
          submissionDate: m.submissionDate,
          marks: m.marksObtained ?? m.marksAwarded ?? 0,
          maximumMarks: m.totalMarks || m.maxMarks || 15,
          wordCount: m.wordCount || 220,
          evaluationStatus: m.evaluationStatus || m.resultStatus || "Pending",
          evaluatorFeedback: m.feedback || m.evaluatorFeedback || "",
          evaluatorName: m.evaluatorName || "Selva Kumar",
          questionText: m.questionText || "",
          submittedAnswer: m.submittedAnswer || "",
          submittedPdfUrl: m.submittedPdfUrl || "",
        })),
      };

      setProfile(localProfile);
      setLoading(false);
    };

    fetchProfile();
  }, [isOpen, student, studentResults]);

  if (!isOpen) return null;

  const info = profile?.personalInfo || {};
  const stats = profile?.academicSummary || {};
  const prelimsHistory = profile?.prelimsHistory || [];
  const mainsHistory = profile?.mainsHistory || [];

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-slate-900/60 backdrop-blur-xs flex justify-end transition-opacity">
      <div className="w-full max-w-2xl bg-white h-full shadow-2xl flex flex-col border-l border-slate-200 animate-in slide-in-from-right duration-200">
        {/* Drawer Header */}
        <div className="p-6 bg-gradient-to-r from-[#0b1528] via-slate-900 to-red-950 text-white relative">
          <button
            onClick={onClose}
            className="absolute top-5 right-5 p-2 rounded-xl bg-white/10 hover:bg-white/20 text-slate-300 hover:text-white transition-colors"
            title="Close Profile"
          >
            <X size={18} />
          </button>

          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            {/* Student Avatar / Photo */}
            <div className="relative">
              {info.avatar ? (
                <img
                  src={info.avatar}
                  alt={info.name}
                  className="w-18 h-18 rounded-2xl object-cover border-2 border-amber-400/80 shadow-md"
                />
              ) : (
                <div className="w-18 h-18 rounded-2xl bg-gradient-to-br from-red-800 to-amber-600 text-white font-black text-2xl flex items-center justify-center border-2 border-amber-400/80 shadow-md">
                  {(info.name || "U")
                    .split(" ")
                    .map((n) => n[0])
                    .slice(0, 2)
                    .join("")
                    .toUpperCase()}
                </div>
              )}
              <span className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-500 border-2 border-[#0b1528] rounded-full flex items-center justify-center text-white" title="Active Aspirant">
                <UserCheck size={10} />
              </span>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-xl font-black text-white truncate">
                  {info.name || "Student Aspirant"}
                </h2>
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold font-mono bg-amber-400/20 text-amber-300 border border-amber-400/40">
                  {info.registrationId || "SIA-2026-9001"}
                </span>
              </div>

              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-300 font-medium">
                <span className="inline-flex items-center gap-1">
                  <MapPin size={13} className="text-amber-400" />
                  {info.location || "Tamil Nadu, India"}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Calendar size={13} className="text-slate-400" />
                  Joined {info.registeredAt ? new Date(info.registeredAt).toLocaleDateString("en-IN", { month: "short", year: "numeric" }) : "2026"}
                </span>
              </div>
            </div>
          </div>

          {/* Working Contact Action Buttons */}
          <div className="mt-5 pt-4 border-t border-white/10 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <a
              href={`mailto:${info.email}`}
              className="flex items-center justify-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl text-xs font-semibold transition-all border border-white/10"
              title="Send Email to Student"
            >
              <Mail size={14} className="text-amber-300" />
              <span className="truncate">{info.email || "student@example.com"}</span>
            </a>

            <a
              href={`tel:${(info.phone || info.mobileNumber || "").replace(/\s+/g, "")}`}
              className="flex items-center justify-center gap-2 px-3 py-2 bg-emerald-600/80 hover:bg-emerald-600 text-white rounded-xl text-xs font-semibold transition-all border border-emerald-500/40"
              title="Call Student Mobile"
            >
              <Phone size={14} className="text-emerald-200" />
              <span>{info.phone || info.mobileNumber || "+91 98401 22334"}</span>
            </a>
          </div>
        </div>

        {/* Academic Performance KPI Bar */}
        <div className="p-4 bg-slate-50 border-b border-slate-200 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
          {mode === "prelims" ? (
            <>
              <div className="bg-white p-2.5 rounded-xl border border-slate-200/80 shadow-2xs">
                <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider">
                  Prelims Attended
                </span>
                <span className="text-lg font-black text-emerald-700">
                  {stats.totalPrelimsAttended ?? 0}
                </span>
              </div>

              <div className="bg-white p-2.5 rounded-xl border border-slate-200/80 shadow-2xs">
                <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider">
                  Prelims Done
                </span>
                <span className="text-lg font-black text-slate-800">
                  {stats.totalPrelimsCompleted ?? 0}
                </span>
              </div>

              <div className="bg-white p-2.5 rounded-xl border border-slate-200/80 shadow-2xs">
                <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider">
                  Total Tests
                </span>
                <span className="text-lg font-black text-blue-700">
                  {prelimsHistory.length}
                </span>
              </div>

              <div className="bg-white p-2.5 rounded-xl border border-slate-200/80 shadow-2xs">
                <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider">
                  Highest Marks
                </span>
                <span className="text-lg font-black text-amber-700">
                  {prelimsHistory.length ? Math.max(...prelimsHistory.map((p) => p.marks || 0)) : 0}
                </span>
              </div>
            </>
          ) : mode === "mains" ? (
            <>
              <div className="bg-white p-2.5 rounded-xl border border-slate-200/80 shadow-2xs">
                <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider">
                  Mains Submitted
                </span>
                <span className="text-lg font-black text-blue-700">
                  {stats.totalMainsSubmissions ?? 0}
                </span>
              </div>

              <div className="bg-white p-2.5 rounded-xl border border-slate-200/80 shadow-2xs">
                <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider">
                  Mains Evaluated
                </span>
                <span className="text-lg font-black text-emerald-700">
                  {stats.totalMainsEvaluated ?? 0}
                </span>
              </div>

              <div className="bg-white p-2.5 rounded-xl border border-slate-200/80 shadow-2xs">
                <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider">
                  Pending Review
                </span>
                <span className="text-lg font-black text-amber-700">
                  {Math.max(0, (stats.totalMainsSubmissions || 0) - (stats.totalMainsEvaluated || 0))}
                </span>
              </div>

              <div className="bg-white p-2.5 rounded-xl border border-slate-200/80 shadow-2xs">
                <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider">
                  Total Submissions
                </span>
                <span className="text-lg font-black text-red-900">
                  {mainsHistory.length}
                </span>
              </div>
            </>
          ) : (
            <>
              <div className="bg-white p-2.5 rounded-xl border border-slate-200/80 shadow-2xs">
                <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider">
                  Prelims Attended
                </span>
                <span className="text-lg font-black text-emerald-700">
                  {stats.totalPrelimsAttended ?? 0}
                </span>
              </div>

              <div className="bg-white p-2.5 rounded-xl border border-slate-200/80 shadow-2xs">
                <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider">
                  Prelims Done
                </span>
                <span className="text-lg font-black text-slate-800">
                  {stats.totalPrelimsCompleted ?? 0}
                </span>
              </div>

              <div className="bg-white p-2.5 rounded-xl border border-slate-200/80 shadow-2xs">
                <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider">
                  Mains Submitted
                </span>
                <span className="text-lg font-black text-blue-700">
                  {stats.totalMainsSubmissions ?? 0}
                </span>
              </div>

              <div className="bg-white p-2.5 rounded-xl border border-slate-200/80 shadow-2xs">
                <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider">
                  Mains Evaluated
                </span>
                <span className="text-lg font-black text-red-900">
                  {stats.totalMainsEvaluated ?? 0}
                </span>
              </div>
            </>
          )}
        </div>

        {/* Performance History Section Header / Tabs */}
        {mode === "prelims" ? (
          <div className="px-6 py-3.5 border-b border-slate-200 flex items-center justify-between text-xs font-bold bg-white text-emerald-800">
            <span className="flex items-center gap-2">
              <CheckCircle2 size={16} className="text-emerald-700" />
              <span>Prelims Test Performance History ({prelimsHistory.length})</span>
            </span>
          </div>
        ) : mode === "mains" ? (
          <div className="px-6 py-3.5 border-b border-slate-200 flex items-center justify-between text-xs font-bold bg-white text-red-900">
            <span className="flex items-center gap-2">
              <BookOpen size={16} className="text-red-900" />
              <span>Mains Initiative Submissions ({mainsHistory.length})</span>
            </span>
          </div>
        ) : (
          <div className="px-6 pt-4 border-b border-slate-200 flex gap-4 text-xs font-bold bg-white">
            <button
              onClick={() => setActiveTab("prelims")}
              className={`pb-3 border-b-2 flex items-center gap-1.5 transition-all cursor-pointer ${
                activeTab === "prelims"
                  ? "border-emerald-700 text-emerald-800"
                  : "border-transparent text-slate-400 hover:text-slate-700"
              }`}
            >
              <CheckCircle2 size={15} />
              <span>Prelims History ({prelimsHistory.length})</span>
            </button>

            <button
              onClick={() => setActiveTab("mains")}
              className={`pb-3 border-b-2 flex items-center gap-1.5 transition-all cursor-pointer ${
                activeTab === "mains"
                  ? "border-red-900 text-red-900"
                  : "border-transparent text-slate-400 hover:text-slate-700"
              }`}
            >
              <BookOpen size={15} />
              <span>Mains Submissions ({mainsHistory.length})</span>
            </button>
          </div>
        )}

        {/* Drawer Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {loading ? (
            <div className="py-12 text-center text-xs text-slate-400 animate-pulse">
              Loading student history from database...
            </div>
          ) : activeTab === "prelims" ? (
            /* PRELIMS HISTORY */
            prelimsHistory.length === 0 ? (
              <div className="py-12 text-center text-slate-400 text-xs">
                No Prelims mock test records found for this student.
              </div>
            ) : (
              <div className="space-y-3">
                {prelimsHistory.map((item, idx) => (
                  <div
                    key={item.id || idx}
                    className="p-4 rounded-xl border border-slate-200 bg-white hover:border-emerald-300 hover:shadow-xs transition-all space-y-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-100">
                          {item.date || "Completed"}
                        </span>
                        <h4 className="text-xs font-bold text-slate-900 mt-1">
                          {item.testName}
                        </h4>
                      </div>

                      <div className="text-right">
                        <span className="text-sm font-black text-emerald-700 block">
                          {item.marks} <span className="text-slate-400 text-[10px] font-normal">/ {item.totalMarks || 10}</span>
                        </span>
                        <span className="text-[10px] font-bold text-slate-500">
                          {item.percentage}%
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-slate-100 text-[11px] text-slate-600">
                      <div className="flex items-center gap-3">
                        <span className="text-emerald-700 font-semibold">
                          ✓ {item.correctAnswers} Correct
                        </span>
                        <span className="text-rose-600 font-semibold">
                          ✗ {item.incorrectAnswers} Incorrect
                        </span>
                        {item.unanswered > 0 && (
                          <span className="text-slate-400">
                            - {item.unanswered} Skipped
                          </span>
                        )}
                      </div>

                      {onInspectPrelimsResult && (
                        <button
                          onClick={() => {
                            onClose();
                            onInspectPrelimsResult(item);
                          }}
                          className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 hover:text-emerald-800"
                        >
                          <span>Inspect Scorecard</span>
                          <ChevronRight size={13} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            /* MAINS HISTORY */
            mainsHistory.length === 0 ? (
              <div className="py-12 text-center text-slate-400 text-xs">
                No Mains initiative submissions found for this student.
              </div>
            ) : (
              <div className="space-y-3">
                {mainsHistory.map((item, idx) => {
                  const isEvaluated = item.evaluationStatus === "Evaluated";
                  return (
                    <div
                      key={item.id || idx}
                      className="p-4 rounded-xl border border-slate-200 bg-white hover:border-red-300 hover:shadow-xs transition-all space-y-3"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-700 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                              {item.gsPaper?.split("(")[0]?.trim() || "GS Paper"}
                            </span>
                            <span
                              className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                                isEvaluated
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                  : "bg-amber-50 text-amber-700 border-amber-200"
                              }`}
                            >
                              {item.evaluationStatus}
                            </span>
                          </div>
                          <h4 className="text-xs font-bold text-slate-900 mt-1">
                            {item.initiativeName}
                          </h4>
                          <span className="text-[10px] text-slate-400 block mt-0.5">
                            Submitted: {item.submissionDate || "Recently"} • {item.wordCount || 200} words
                          </span>
                        </div>

                        <div className="text-right">
                          <span className="text-sm font-black text-red-900 block">
                            {item.marks} <span className="text-slate-400 text-[10px] font-normal">/ {item.maximumMarks || 15}</span>
                          </span>
                          <span className="text-[10px] text-slate-400 block">
                            Max {item.maximumMarks || 15}
                          </span>
                        </div>
                      </div>

                      {item.evaluatorFeedback && (
                        <div className="p-2.5 rounded-lg bg-amber-50/60 border border-amber-200/70 text-[11px] text-slate-700">
                          <strong className="text-amber-900 block font-semibold mb-0.5">
                            Feedback from Evaluator ({item.evaluatorName || "Selva Sir"}):
                          </strong>
                          <p className="line-clamp-2 italic">"{item.evaluatorFeedback}"</p>
                        </div>
                      )}

                      <div className="flex items-center justify-end pt-2 border-t border-slate-100">
                        {onInspectMainsSubmission && (
                          <button
                            onClick={() => {
                              onClose();
                              onInspectMainsSubmission(item);
                            }}
                            className="inline-flex items-center gap-1 text-[11px] font-bold text-red-900 hover:text-red-800"
                          >
                            <span>{isEvaluated ? "View Evaluation" : "Evaluate Answer"}</span>
                            <ChevronRight size={13} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}
        </div>

        {/* Drawer Footer */}
        <div className="p-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between text-xs">
          <span className="text-slate-500 text-[11px]">
            Selva's IAS Academy • Student Administration
          </span>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl text-xs transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
